import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Shield, CheckCircle, Lock, FileText, Users, 
  Building2, Scale, Eye, Database, Server,
  ArrowRight, Download, ExternalLink
} from "lucide-react";
import { API_BASE_URL } from "../lib/config";

const API_URL = API_BASE_URL;

const Compliance = () => {
  const [complianceData, setComplianceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchComplianceInfo();
  }, []);

  const fetchComplianceInfo = async () => {
    try {
      const res = await fetch(`${API_URL}/compliance/info`);
      if (res.ok) {
        setComplianceData(await res.json());
      }
    } catch (error) {
      console.error("Error fetching compliance info:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">A</span>
            </div>
            <span className="font-heading font-bold text-xl text-slate-800">Clear Path Testing Solutions</span>
          </Link>
          <Link 
            to="/contact" 
            className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700"
          >
            Contact Us
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl mb-4">
            Texas School Compliance
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Clear Path Testing Solutions is built from the ground up to meet Texas K-12 data privacy 
            and AI governance requirements.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="#compliance-standards" 
              className="px-6 py-3 bg-white text-slate-900 font-bold rounded-xl hover:bg-slate-100 transition-colors"
            >
              View Compliance Standards
            </a>
            <Link 
              to="/texas-schools" 
              className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors flex items-center gap-2"
            >
              District Sales Info <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Key Compliance Badges */}
      <section className="py-12 px-4 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { name: "Texas Student Privacy Act", icon: Lock, color: "bg-blue-500" },
              { name: "TRAIGA Ready", icon: Eye, color: "bg-purple-500" },
              { name: "TDPSA Compliant", icon: FileText, color: "bg-green-500" },
              { name: "COPPA Compliant", icon: Users, color: "bg-orange-500" },
              { name: "FERPA Compliant", icon: Building2, color: "bg-indigo-500" }
            ].map((badge, i) => (
              <div key={i} className="flex flex-col items-center text-center p-4">
                <div className={`w-14 h-14 ${badge.color} rounded-xl flex items-center justify-center mb-3`}>
                  <badge.icon className="w-7 h-7 text-white" />
                </div>
                <p className="font-bold text-slate-800 text-sm">{badge.name}</p>
                <CheckCircle className="w-5 h-5 text-green-500 mt-2" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Standards Detail */}
      <section id="compliance-standards" className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading font-bold text-3xl text-slate-800 text-center mb-12">
            Compliance Standards
          </h2>
          
          <div className="space-y-6">
            {complianceData?.compliance_standards?.map((standard, i) => (
              <div key={i} className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-heading font-bold text-lg text-slate-800">{standard.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          standard.status === "Compliant" ? "bg-green-100 text-green-700" :
                          standard.status === "Ready" ? "bg-blue-100 text-blue-700" :
                          "bg-yellow-100 text-yellow-700"
                        }`}>
                          {standard.status}
                        </span>
                      </div>
                      <p className="text-slate-500 text-sm mb-3">{standard.reference}</p>
                      <p className="text-slate-700">{standard.details}</p>
                      {standard.effective_date && (
                        <p className="text-sm text-indigo-600 mt-2 font-medium">
                          Effective: {standard.effective_date}
                        </p>
                      )}
                    </div>
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Handling */}
      <section className="py-16 px-4 bg-slate-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading font-bold text-3xl text-slate-800 text-center mb-4">
            Data Handling & Security
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Your students' data is protected with enterprise-grade security measures and strict handling policies.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* PII Protection */}
            <div className="bg-white rounded-2xl p-6 border-2 border-slate-200">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">PII Protection</h3>
              <p className="text-slate-600 text-sm">
                {complianceData?.data_handling?.pii_protection}
              </p>
            </div>

            {/* Encryption */}
            <div className="bg-white rounded-2xl p-6 border-2 border-slate-200">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">Encryption</h3>
              <p className="text-slate-600 text-sm">
                {complianceData?.data_handling?.encryption}
              </p>
            </div>

            {/* Data Location */}
            <div className="bg-white rounded-2xl p-6 border-2 border-slate-200">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Server className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">Data Location</h3>
              <p className="text-slate-600 text-sm">
                {complianceData?.data_handling?.data_location}
              </p>
            </div>

            {/* Third Party */}
            <div className="bg-white rounded-2xl p-6 border-2 border-slate-200">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">No Third-Party Sharing</h3>
              <p className="text-slate-600 text-sm">
                {complianceData?.data_handling?.third_party_sharing}
              </p>
            </div>
          </div>

          {/* Data Retention Policy */}
          <div className="mt-8 bg-white rounded-2xl p-6 border-2 border-slate-200">
            <h3 className="font-heading font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-indigo-600" />
              Data Retention Policy
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {complianceData?.data_handling?.data_retention && Object.entries(complianceData.data_handling.data_retention).map(([key, days]) => (
                <div key={key} className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-slate-500 text-sm capitalize">{key.replace(/_/g, ' ')}</p>
                  <p className="font-bold text-xl text-slate-800 mt-1">
                    {days === -1 ? "Indefinite" : `${days} days`}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI Governance */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading font-bold text-3xl text-slate-800 text-center mb-4">
            AI Governance (TRAIGA Ready)
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Our AI tutor "Archie" is built with responsible AI principles, fully compliant with 
            Texas HB 149 requirements effective January 2026.
          </p>

          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border-2 border-indigo-200">
            <div className="grid md:grid-cols-2 gap-6">
              {complianceData?.ai_governance && Object.entries(complianceData.ai_governance).map(([key, value]) => (
                <div key={key} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-800 capitalize">{key.replace(/_/g, ' ')}</p>
                    <p className="text-slate-600 text-sm">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Security Measures */}
      <section className="py-16 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading font-bold text-3xl text-center mb-12">
            Security Measures
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {complianceData?.security_measures?.map((measure, i) => (
              <div key={i} className="bg-slate-800 rounded-xl p-5 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="text-slate-200">{measure}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl mb-4">
            Ready to Bring Clear Path to Your District?
          </h2>
          <p className="text-indigo-100 mb-8 max-w-2xl mx-auto">
            Download our district compliance checklist or schedule a demo with our team.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/texas-schools" 
              className="px-6 py-3 bg-white text-indigo-600 font-bold rounded-xl hover:bg-indigo-50 transition-colors flex items-center gap-2"
            >
              <Download className="w-5 h-5" />
              District Sales Package
            </Link>
            <Link 
              to="/contact" 
              className="px-6 py-3 bg-indigo-500 text-white font-bold rounded-xl hover:bg-indigo-400 transition-colors"
            >
              Schedule a Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Footer Contact */}
      <section className="py-12 px-4 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="font-heading font-bold text-xl text-slate-800 mb-4">
            Compliance Questions?
          </h3>
          <div className="flex flex-wrap justify-center gap-6 text-slate-600">
            <a href={`mailto:${complianceData?.contact?.privacy_officer}`} className="hover:text-indigo-600 flex items-center gap-2">
              <Scale className="w-4 h-4" />
              Privacy Officer
            </a>
            <a href={`mailto:${complianceData?.contact?.security_team}`} className="hover:text-indigo-600 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Security Team
            </a>
            <a href={`mailto:${complianceData?.contact?.general_inquiries}`} className="hover:text-indigo-600 flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              General Inquiries
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Compliance;
